import React, { useState } from 'react';
import { BdsBanner, BdsButtonIcon, BdsCheckbox, BdsChipTag, BdsGrid, BdsIcon, BdsPaper, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import { formatFileSize, getDocumentIconName } from 'utils/fileUtil';
import { AccordionContainer, DocumentName, FileNameGrid, FlexGrid, TagWrapper } from './styles';
import { PreProcessConfig } from 'models/knowledgeBase/Document';
import { useContentLocalizer } from 'hooks/useContentLocalizer';
import { localization } from './localization';

interface DocumentAccordionProps {
    config: PreProcessConfig;
    isOpen: boolean;
    isGroundingPreprocessingEnabled: boolean;
    disabled?: boolean;
    onDelete?: (fileName: string) => void;
    onTogglePreProcess?: (fileName: string, type: 'optimization' | 'indexing' | 'contextualization', value: boolean) => void;
}

export const DocumentAccordion: React.FC<DocumentAccordionProps> = ({ config, isOpen, isGroundingPreprocessingEnabled, disabled, onDelete, onTogglePreProcess }) => {
    const [isExpanded, setIsExpanded] = useState(isOpen);
    const translate = useContentLocalizer(localization);

    const getExtension = (fileName: string): string => {
        const parts = fileName.split('.');
        return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
    };

    return (
        <AccordionContainer direction="column" gap="half">
            <BdsGrid gap="2" alignItems="center" justifyContent="space-between">
                <FileNameGrid gap="1" alignItems="center">
                    <BdsIcon theme="outline" name={getDocumentIconName(getExtension(config.file.name))} size="medium" />
                    <DocumentName variant="fs-14" bold="bold">{config.file.name}</DocumentName>
                </FileNameGrid>
                <BdsTypo variant="fs-14" bold="bold">{formatFileSize(config.file.size)}</BdsTypo>
                <BdsGrid gap="1" alignItems="center">
                    <TagWrapper>
                        {(config.optimization || config.indexing || config.contextualization) && (
                            <BdsChipTag color="info">{translate.preProcessingTag}</BdsChipTag>
                        )}
                    </TagWrapper>
                    <BdsButtonIcon
                        onClick={() => {
                            onDelete && onDelete(config.file.name);
                        }}
                        size="short"
                        variant="secondary"
                        icon="trash"
                        disabled={disabled}
                    ></BdsButtonIcon>
                    <BdsButtonIcon
                        onClick={() => {
                            setIsExpanded(!isExpanded);
                        }}
                        size="short"
                        variant="secondary"
                        icon={isExpanded ? 'arrow-up' : 'arrow-down'}
                    ></BdsButtonIcon>
                </BdsGrid>
            </BdsGrid>
            {isExpanded && (
                <BdsGrid direction="column" gap="1">
                    <BdsTypo variant="fs-14" bold="bold">{translate.preProcessingsTitle}</BdsTypo>
                    <BdsGrid gap="2">
                        <FlexGrid>
                            <BdsPaper elevation="none" bgColor="surface-1">
                                <BdsGrid padding="2" gap="1" direction="column">
                                    <BdsGrid justifyContent="space-between">
                                        <BdsTypo variant="fs-14" bold="bold">{translate.optimization.title}</BdsTypo>
                                        <BdsCheckbox name="optimization" onBdsChange={() => onTogglePreProcess && onTogglePreProcess(config.file.name, 'optimization', !config.optimization)} checked={config.optimization} disabled={disabled} refer={''} label={''} />
                                    </BdsGrid>
                                    <BdsTypo variant="fs-12">{translate.optimization.description}</BdsTypo>
                                </BdsGrid>
                            </BdsPaper>
                        </FlexGrid>
                        { isGroundingPreprocessingEnabled &&
                            <>
                                <FlexGrid>
                                    <BdsPaper elevation="none" bgColor="surface-1">
                                        <BdsGrid padding="2" gap="1" direction="column">
                                            <BdsGrid justifyContent="space-between">
                                                <BdsTypo variant="fs-14" bold="bold">{translate.indexing.title}</BdsTypo>
                                                <BdsCheckbox name="indexing" onBdsChange={() => onTogglePreProcess && onTogglePreProcess(config.file.name, 'indexing', !config.indexing)} checked={config.indexing} disabled={disabled} refer={''} label={''} />
                                            </BdsGrid>
                                            <BdsTypo variant="fs-12">{translate.indexing.description}</BdsTypo>
                                            <BdsChipTag color="outline" icon="sparkle-ai">{translate.usesAi}</BdsChipTag>
                                        </BdsGrid>
                                    </BdsPaper>
                                </FlexGrid>
                                <FlexGrid>
                                    <BdsPaper elevation="none" bgColor="surface-1">
                                        <BdsGrid padding="2" gap="1" direction="column">
                                            <BdsGrid justifyContent="space-between">
                                                <BdsTypo variant="fs-14" bold="bold">{translate.contextualization.title}</BdsTypo>
                                                <BdsCheckbox name="contextualization" onBdsChange={() => onTogglePreProcess && onTogglePreProcess(config.file.name, 'contextualization', !config.contextualization)} checked={config.contextualization} disabled={disabled} refer={''} label={''} />
                                            </BdsGrid>
                                            <BdsTypo variant="fs-12">{translate.contextualization.description}</BdsTypo>
                                            <BdsChipTag color="outline" icon="sparkle-ai">{translate.usesAi}</BdsChipTag>
                                        </BdsGrid>
                                    </BdsPaper>
                                </FlexGrid>
                            </>
                        }   
                    </BdsGrid>
                    {(isGroundingPreprocessingEnabled && config.indexing && config.contextualization) && (
                        <BdsBanner context="inside" variant="warning">
                            <BdsTypo variant="fs-14" bold="bold">{translate.indexingContextualizationWarning}</BdsTypo>
                        </BdsBanner>
                    )}
                </BdsGrid>
            )}
        </AccordionContainer>
    );
};
